import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';

/**
 * Hooks für die Routinen-Library (Sprint 5, WT-501/WT-503; Playbook-Editor Sprint 8).
 *
 * Backend-Endpunkte:
 *   GET  /routines                                -> RoutineListResponse  { data, total }
 *   POST /routines/resync                         -> RoutineResyncResponse (Mount neu einlesen)
 *   GET  /routines/{slug}                         -> RoutineDetailResponse (inkl. playbook_source)
 *   POST /routines/{code}/playbook/validate       -> PlaybookValidateResponse
 *   PUT  /routines/{code}/playbook                -> PlaybookSaveResponse (git-Commit)
 *
 * Wir definieren die Types lokal (statt aus `api.gen.ts`), weil die OpenAPI-
 * Spec noch nicht aus dem Backend regeneriert wurde.
 */

export const routineKeys = {
  all: ['routines'] as const,
  list: ['routines', 'list'] as const,
  detail: (slug: string) => ['routines', 'detail', slug] as const,
};

export interface Routine {
  id: string;
  code: string;
  display_name: string;
  description?: string | null;
  ansible_playbook_path?: string | null;
  applicable_service_types: string[];
  /**
   * Betriebssystem-Familien (`systems.os_family`), für die die Routine gilt -
   * ODER-verknuepft mit `applicable_service_types`. Damit lässt sich "alle
   * Linux-Systeme" binden, ohne dass an jedem System ein Service-Typ gepflegt
   * sein muss (Stand 09.09.2026: bei der Mehrheit der Linux-Flotte fehlte er).
   *
   * Sind BEIDE Listen leer, passt die Routine auf KEIN System. Bis zum
   * 10.09.2026 war die leere Typenliste eine stille Wildcard und traf jedes
   * System mit SSH-Profil, auch OPNsense und Windows.
   *
   * Optional getypt, weil ein älterer Server das Feld nicht liefert; "fehlt"
   * wird wie "leer" behandelt.
   */
  applicable_os_families?: string[] | null;
  parameters_schema: Record<string, unknown>;
  default_parameters: Record<string, unknown>;
  requires_snapshot: boolean;
  is_destructive: boolean;
  estimated_duration_seconds?: number | null;
  /**
   * Reifegrad: experimental | beta | production | deprecated. Sagt, wie viel
   * Erprobung BELEGT ist, nicht wie gut die Routine gemeint ist. Quelle ist der
   * Playbook-Meta-Header (`tier`); ohne Angabe gilt `beta`. Label, Ton und
   * Bedeutung stehen in `@/components/routines/tier`.
   */
  tier: string;
  /**
   * Galaxy-Collections, ohne die das Playbook nicht läuft (z. B.
   * `["ansible.windows"]`). Leere Liste = kommt mit ansible-core aus, also
   * "nur Bordmittel" - der Normalfall. Quelle ist der Playbook-Meta-Header
   * (`required_collections`).
   *
   * Optional getypt, weil ein älterer Server das Feld nicht liefert; die
   * Anzeige behandelt "fehlt" wie "leer" (siehe
   * `@/components/routines/collections`).
   */
  required_collections?: string[] | null;
  /**
   * Abarbeitungs-Reihenfolge, kleiner = läuft früher (Default 100).
   * Quelle ist der Meta-Header des Playbooks: die Registry upserted die
   * Routinen bei jedem Backend-Start daraus und würde einen im UI
   * getippten DB-Wert wieder überschreiben.
   */
  execution_order: number;
  created_at: string;
  updated_at: string;
}

/** Backend-Default; greift auch, wenn ein alter Server das Feld nicht liefert. */
export const DEFAULT_EXECUTION_ORDER = 100;

/**
 * Reihenfolge-Wert einer Routine, tolerant gegenüber fehlendem/kaputtem Feld -
 * ein undefined würde sonst als NaN durch jeden Vergleich rutschen und die
 * Liste in zufälliger Ordnung zeigen.
 */
export function executionOrderOf(routine: {
  execution_order?: number | null;
}): number {
  return typeof routine.execution_order === 'number' &&
    Number.isFinite(routine.execution_order)
    ? routine.execution_order
    : DEFAULT_EXECUTION_ORDER;
}

/** Anzeige-/Ausfuehrungsfolge: Reihenfolge zuerst, dann Anzeigename. */
export function compareRoutineOrder(a: Routine, b: Routine): number {
  const diff = executionOrderOf(a) - executionOrderOf(b);
  if (diff !== 0) return diff;
  return a.display_name.localeCompare(b.display_name, 'de');
}

export interface RoutineDetail extends Routine {
  playbook_source?: string | null;
}

export interface RoutineListResponse {
  data: Routine[];
  total: number;
}

export function useRoutines(): ReturnType<typeof useQuery<RoutineListResponse>> {
  return useQuery<RoutineListResponse>({
    queryKey: routineKeys.list,
    queryFn: () => api.get<RoutineListResponse>('/routines'),
    staleTime: 60_000,
  });
}

export function useRoutine(
  slug: string | undefined
): ReturnType<typeof useQuery<RoutineDetail>> {
  return useQuery<RoutineDetail>({
    queryKey: routineKeys.detail(slug ?? ''),
    queryFn: () => api.get<RoutineDetail>(`/routines/${slug ?? ''}`),
    enabled: Boolean(slug),
    staleTime: 60_000,
  });
}

/* ------------------------------------------------------------------ */
/* Routinen neu einlesen (10.09.2026).                                 */
/* ------------------------------------------------------------------ */

/**
 * Ein Feld, das sich beim Einlesen geändert hat.
 *
 * `feld` ist der technische Spaltenname (`display_name`, `tier`, ...) - er
 * passt damit zu dem, was im Meta-Header des Playbooks steht. Die deutsche
 * Beschriftung dazu steht in `RESYNC_FELD_LABEL`.
 */
export interface RoutineResyncFeldAenderung {
  feld: string;
  /** null = war leer. Listen kommen als "a, b". */
  alt?: string | null;
  neu?: string | null;
}

export interface RoutineResyncGeaendert {
  slug: string;
  aenderungen: RoutineResyncFeldAenderung[];
}

export interface RoutineResyncFehler {
  /** Dateiname im Routinen-Mount, ohne Pfad. */
  datei: string;
  fehler: string;
  /** 'parse' oder 'doppelter_slug'. */
  art?: string | null;
}

export interface RoutineResyncResponse {
  verzeichnis: string;
  gelesen: number;
  neu: string[];
  geaendert: RoutineResyncGeaendert[];
  unveraendert: string[];
  fehler: RoutineResyncFehler[];
  /** Fachliche Hinweise, z. B. "Routine erreicht kein System". */
  warnungen?: string[] | null;
  /** true = der Mount fehlt ganz (nicht: er ist leer). */
  verzeichnis_fehlt?: boolean | null;
}

/**
 * Beschriftungen der Felder im Ergebnisdialog.
 *
 * Bewusst hier und nicht im Backend: Wer den Dialog liest, hat gerade den
 * Meta-Header bearbeitet - der technische Name muss sichtbar bleiben, die
 * deutsche Beschriftung kommt daneben. Ein unbekannter Schlüssel fällt auf
 * den Rohnamen zurück, damit ein neues Feld nicht als leere Zeile erscheint.
 */
export const RESYNC_FELD_LABEL: Record<string, string> = {
  display_name: 'Anzeigename',
  description: 'Beschreibung',
  ansible_playbook_path: 'Playbook-Pfad',
  applicable_service_types: 'Service-Typen',
  applicable_os_families: 'OS-Familien',
  requires_snapshot: 'Snapshot Pflicht',
  is_destructive: 'Destruktiv',
  estimated_duration_seconds: 'Zeitbudget (s)',
  execution_order: 'Reihenfolge',
  auto_select: 'Vorausgewählt',
  customer_report: 'Im Kundenbericht',
  tier: 'Reifegrad',
  required_collections: 'Collections',
};

export function resyncFeldLabel(feld: string): string {
  return RESYNC_FELD_LABEL[feld] ?? feld;
}

/**
 * Löst `POST /routines/resync` aus und zieht die Library danach neu.
 *
 * Ohne das Invalidieren zeigte die Liste den Stand von vor dem Einlesen -
 * also genau das, was der Knopf gerade beheben sollte.
 */
export function useResyncRoutines(): ReturnType<
  typeof useMutation<RoutineResyncResponse, Error, void>
> {
  const qc = useQueryClient();
  return useMutation<RoutineResyncResponse, Error, void>({
    mutationFn: () => api.post<RoutineResyncResponse>('/routines/resync'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: routineKeys.all });
    },
  });
}

/* ------------------------------------------------------------------ */
/* Playbook-Editor (Sprint 8): Validieren + Speichern mit Git-Commit. */
/* ------------------------------------------------------------------ */

export type PlaybookIssueSeverity = 'error' | 'warning';
export type PlaybookIssueSource = 'yaml' | 'meta' | 'ansible';

export interface PlaybookIssue {
  /** 1-basierte Zeile; null wenn der Fehler keiner Zeile zuzuordnen ist. */
  line: number | null;
  column: number | null;
  severity: PlaybookIssueSeverity;
  source: PlaybookIssueSource;
  message: string;
}

export interface PlaybookValidateResponse {
  valid: boolean;
  issues: PlaybookIssue[];
  meta: Record<string, unknown> | null;
}

export interface SavePlaybookInput {
  content: string;
  /** Optionale Git-Commit-Message; Backend hat einen Default. */
  message?: string | null;
  /**
   * sha256 des Standes, auf dem die Bearbeitung basiert. null/weglassen =
   * kein Konflikt-Schutz (der GET-Detail-Endpunkt liefert aktuell keinen
   * sha256, erst die PUT-Response).
   */
  expectedSha256?: string | null;
}

export interface PlaybookSaveResponse {
  ok: true;
  commit: string | null;
  issues: PlaybookIssue[];
  sha256: string;
}

interface SavePlaybookBody {
  content: string;
  message?: string | null;
  expected_sha256?: string | null;
}

export function useValidatePlaybook(
  code: string
): ReturnType<typeof useMutation<PlaybookValidateResponse, Error, string>> {
  return useMutation<PlaybookValidateResponse, Error, string>({
    mutationFn: (content) =>
      api.post<PlaybookValidateResponse, { content: string }>(
        `/routines/${encodeURIComponent(code)}/playbook/validate`,
        { content }
      ),
  });
}

export function useSavePlaybook(
  slug: string,
  code: string
): ReturnType<typeof useMutation<PlaybookSaveResponse, Error, SavePlaybookInput>> {
  const qc = useQueryClient();
  return useMutation<PlaybookSaveResponse, Error, SavePlaybookInput>({
    mutationFn: ({ content, message, expectedSha256 }) => {
      const body: SavePlaybookBody = { content };
      if (message != null && message.trim() !== '') body.message = message.trim();
      if (expectedSha256 != null) body.expected_sha256 = expectedSha256;
      return api.put<PlaybookSaveResponse, SavePlaybookBody>(
        `/routines/${encodeURIComponent(code)}/playbook`,
        body
      );
    },
    onSuccess: (_res, { content }) => {
      // Detail-Cache sofort auf den gespeicherten Stand heben (kein Flash
      // von altem Source), danach kanonisch nachladen (updated_at etc.).
      qc.setQueryData<RoutineDetail>(routineKeys.detail(slug), (old) =>
        old ? { ...old, playbook_source: content } : old
      );
      void qc.invalidateQueries({ queryKey: routineKeys.detail(slug) });
      void qc.invalidateQueries({ queryKey: routineKeys.list });
    },
  });
}

/**
 * Zieht Playbook-Issues aus einem 400er-Fehlerbody (PUT validiert serverseitig
 * nochmal). Tolerant gegenüber abweichenden Bodies - liefert dann [].
 */
export function extractPlaybookIssues(err: unknown): PlaybookIssue[] {
  if (!(err instanceof ApiError) || err.status !== 400) return [];
  const raw = err.raw;
  if (typeof raw !== 'object' || raw === null) return [];
  const issues = (raw as { issues?: unknown }).issues;
  if (!Array.isArray(issues)) return [];
  return issues.filter(
    (i): i is PlaybookIssue =>
      typeof i === 'object' &&
      i !== null &&
      typeof (i as PlaybookIssue).message === 'string' &&
      ((i as PlaybookIssue).severity === 'error' ||
        (i as PlaybookIssue).severity === 'warning')
  );
}
